import { NavigateTargetType } from '@servicetitan/contactcenter-agent-inbox-mfe-loader';
import type { NavigateTarget } from '@servicetitan/contactcenter-agent-inbox-mfe-loader';
import { resolveTenantId } from '@modules/AuthModule/lib/resolveOrgNodeId';
import type { IContactCenterTenant } from '@modules/AuthModule/lib/types';
import {
  customerLinkToMonolith,
  jobBookingLinkToMonolith,
} from '@lib/utils/tenantLinks';

/**
 * Resolves the monolith URL a CustomerPanel/InboxMfe `NavigateTarget` link-out
 * should open, or `null` when the target's OrgNode Guid matches none of the
 * user's tenants (don't navigate to a broken link). Shared by every host
 * (InboxTextsPage, CustomerMfeAdapter, ...) wiring `onNavigate` — keeps the
 * NavigateTargetType switch + tenantId bridge in one place instead of a
 * per-host copy.
 *
 * Deliberately its own file rather than living in tenantLinks.ts: tenantLinks.ts
 * is imported across many unrelated routes (monitoring, reports, settings, ...),
 * and pulling in @servicetitan/contactcenter-agent-inbox-mfe-loader for
 * NavigateTarget/NavigateTargetType would make it a transitive dependency of
 * all of them. This file depends on tenantLinks.ts's URL builders, never the
 * other way around.
 */
export function resolveNavigateUrl(
  target: NavigateTarget,
  tenants: ReadonlyArray<IContactCenterTenant>,
): string | null {
  // The monolith tenant-redirect URL takes a numeric tenant id, not an
  // OrgNode Guid — bridge back via the user's tenant list.
  const tenantId = resolveTenantId(tenants, target.orgNodeId);
  if (!tenantId) {
    return null;
  }
  const customerId = String(target.customerId);
  switch (target.type) {
    case NavigateTargetType.Job:
      return target.jobId
        ? jobBookingLinkToMonolith(tenantId, target.jobId)
        : customerLinkToMonolith(tenantId, customerId);
    case NavigateTargetType.Customer:
    case NavigateTargetType.CustomerJobs:
    case NavigateTargetType.CustomerEquipment:
    case NavigateTargetType.CustomerBilling:
    default:
      return customerLinkToMonolith(tenantId, customerId);
  }
}
